import React, { Component } from 'react';
import { useLocation } from 'react-router-dom';

export const SITE_URL = 'https://rudrakshpathak.com';
export const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const DEFAULT_TITLE = 'Rudraksh Pathak | Senior Software Engineer & Application Architect';
const DEFAULT_DESCRIPTION =
  'Rudraksh Pathak — Senior Software Engineer and Application Architect with 10+ years building highly available cloud platforms, distributed systems, and high-performing engineering teams.';

export const PAGE_SEO = {
  '/': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    type: 'profile',
    jsonLdType: 'ProfilePage',
  },
  '/showcase': {
    title: 'Project Showcase | Rudraksh Pathak',
    description:
      'Explore software applications, open-source SDKs, developer tools, and npm packages built by Rudraksh Pathak — AWS, Node.js, React, and cloud architecture.',
    type: 'website',
    jsonLdType: 'CollectionPage',
  },
  '/bragging-rights': {
    title: 'Career Highlights | Rudraksh Pathak',
    description:
      'Career highlights of Rudraksh Pathak — Microsoft Student Partner, IndiBlogger award winner, Zee News feature, and open-source community contributions.',
    type: 'website',
    jsonLdType: 'CollectionPage',
  },
  '/videos': {
    title: 'Videos & Tutorials | Rudraksh Pathak',
    description:
      'Watch technical tutorials and hardware reviews by Rudraksh Pathak — SQL Server, Android Studio, Windows, and developer walkthroughs on YouTube.',
    type: 'website',
    jsonLdType: 'CollectionPage',
  },
};

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel, href, extra = {}) {
  const extraSelector = Object.entries(extra)
    .map(([name, value]) => `[${name}="${value}"]`)
    .join('');
  let el = document.head.querySelector(`link[rel="${rel}"]${extraSelector}`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
  Object.entries(extra).forEach(([name, value]) => {
    el.setAttribute(name, value);
  });
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.type = 'application/ld+json';
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function breadcrumbList(pathname, title) {
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: `${SITE_URL}/`,
    },
  ];

  if (pathname !== '/') {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name: title.replace(' | Rudraksh Pathak', ''),
      item: `${SITE_URL}${pathname}`,
    });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

function applySeo(pathname) {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  const page = PAGE_SEO[normalized] || PAGE_SEO['/'];
  const canonical = `${SITE_URL}${normalized === '/' ? '/' : normalized}`;

  document.title = page.title;

  upsertMeta('name', 'description', page.description);
  upsertMeta('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1');

  upsertMeta('property', 'og:type', page.type);
  upsertMeta('property', 'og:site_name', 'Rudraksh Pathak');
  upsertMeta('property', 'og:locale', 'en_US');
  upsertMeta('property', 'og:url', canonical);
  upsertMeta('property', 'og:title', page.title);
  upsertMeta('property', 'og:description', page.description);
  upsertMeta('property', 'og:image', DEFAULT_IMAGE);
  upsertMeta('property', 'og:image:secure_url', DEFAULT_IMAGE);
  upsertMeta('property', 'og:image:alt', page.title);

  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', page.title);
  upsertMeta('name', 'twitter:description', page.description);
  upsertMeta('name', 'twitter:image', DEFAULT_IMAGE);
  upsertMeta('name', 'twitter:url', canonical);

  upsertLink('canonical', canonical);
  upsertLink('alternate', canonical, { hreflang: 'en' });
  upsertLink('alternate', canonical, { hreflang: 'x-default' });

  upsertJsonLd('page-jsonld', {
    '@context': 'https://schema.org',
    '@type': page.jsonLdType,
    '@id': `${canonical}#webpage`,
    url: canonical,
    name: page.title,
    description: page.description,
    inLanguage: 'en',
    isPartOf: { '@id': `${SITE_URL}/#website` },
    about: { '@id': `${SITE_URL}/#person` },
    primaryImageOfPage: {
      '@type': 'ImageObject',
      url: DEFAULT_IMAGE,
    },
  });

  upsertJsonLd('breadcrumb-jsonld', breadcrumbList(normalized, page.title));
}

class SeoTracker extends Component {
  componentDidMount() {
    applySeo(this.props.pathname);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.pathname !== this.props.pathname) {
      applySeo(this.props.pathname);
    }
  }

  render() {
    return null;
  }
}

function Seo() {
  const { pathname } = useLocation();
  return <SeoTracker pathname={pathname} />;
}

export default Seo;
